/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { platformRequestOption } from 'hooks/PlatformContext';
import { IApiRequest } from 'interfaces/apiRequestInterface';
import { clearAuthSession } from 'utils/auth';

type RequestHeaders = {
  [key: string]: string;
};

const apiRequest = async ({
  url,
  payload,
  method,
  params,
  responseType = 'json',
}: IApiRequest): Promise<AxiosResponse> => {
  const accessToken = localStorage.getItem('accessToken');
  const currentLanguage = localStorage.getItem('currentLanguage') || 'en';

  const requestHeader: RequestHeaders = platformRequestOption.requestHeader({
    token: accessToken || '',
  });

  requestHeader['Accept-Language'] = currentLanguage;

  const selectedShopId = localStorage.getItem('shop_id') || localStorage.getItem('active_shop_id');
  const selectedBranchId =
    localStorage.getItem('fuko_selected_branch_id') ||
    localStorage.getItem('active_branch_id') ||
    localStorage.getItem('branch_id');
  const businessType = localStorage.getItem('business_type') || localStorage.getItem('shop_business_type');

  if (selectedShopId && !requestHeader['X-Business-ID']) {
    requestHeader['X-Business-ID'] = selectedShopId;
    requestHeader['X-Shop-ID'] = selectedShopId;
  }
  if (selectedBranchId && !requestHeader['X-Branch-ID']) {
    requestHeader['X-Branch-ID'] = selectedBranchId;
  }
  if (businessType && !requestHeader['X-Business-Type']) {
    requestHeader['X-Business-Type'] = businessType;
  }

  const currentCurrency =
    localStorage.getItem('currency_code') ||
    localStorage.getItem('currency') ||
    localStorage.getItem('default_currency');
  if (currentCurrency && !requestHeader['X-Currency']) {
    requestHeader['X-Currency'] = currentCurrency;
    requestHeader['X-Shop-Currency'] = currentCurrency;
  }

  const cachedUserStr = localStorage.getItem('fuko_cached_user_data') || localStorage.getItem('user');
  let currentUserId = '';
  if (cachedUserStr) {
    try {
      const parsed = JSON.parse(cachedUserStr);
      currentUserId = parsed.id || parsed.user_id || parsed.user?.id || '';
    } catch {
      // Ignore parsing error
    }
  }

  if (currentUserId && !requestHeader['X-User-ID']) {
    requestHeader['X-User-ID'] = String(currentUserId);
  }




  const isFormData = payload instanceof FormData;

  if (isFormData && requestHeader['Content-Type']) {
    delete requestHeader['Content-Type'];
  }

  const requestData = isFormData
    ? payload
    : {
      ...platformRequestOption.requestData,
      ...(payload as Record<string, unknown>),
    };

  // Determine if the URL is absolute and normalize relative paths to prevent double /api/api
  const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://');
  const baseUrl = (platformRequestOption?.url || '').replace(/\/$/, '');
  let relativeUrl = url.startsWith('/') ? url.slice(1) : url;
  if (baseUrl.endsWith('/api') && (relativeUrl.startsWith('api/') || relativeUrl === 'api')) {
    relativeUrl = relativeUrl.replace(/^api\/?/, '');
  }
  const finalUrl = isAbsoluteUrl ? url : `${baseUrl}/${relativeUrl}`;

  const commonOptions: AxiosRequestConfig = {
    method: method,
    headers: requestHeader,
    url: finalUrl,
    withCredentials: true,
    responseType: responseType,
    params: params,
  };

  const postRequestOptions: AxiosRequestConfig = {
    ...commonOptions,
    data: isFormData ? requestData : JSON.stringify(requestData),
  };

  const fetchRequestOptions: AxiosRequestConfig = {
    ...commonOptions,
  };

  const config: AxiosRequestConfig = [
    'POST',
    'PUT',
    'DELETE',
    'PATCH',
  ].includes(method)
    ? postRequestOptions
    : fetchRequestOptions;

  const startTime = Date.now();
  const logEntry: any = {
    id: Math.random().toString(36).substring(2, 9),
    url: finalUrl,
    method: method,
    params: params || null,
    payload: isFormData ? '[FormData]' : (payload || null),
    headers: requestHeader,
    timestamp: new Date().toLocaleTimeString(),
  };

  try {
    const response = await axios.request(config);
    logEntry.status = response.status;
    logEntry.statusText = response.statusText;
    logEntry.response = response.data;
    logEntry.latency = Date.now() - startTime;
    logEntry.isError = false;
    
    const logs = (window as any).__apiLogs || [];
    logs.unshift(logEntry);
    (window as any).__apiLogs = logs.slice(0, 100);

    window.dispatchEvent(new CustomEvent('__apiLogsUpdated'));

    return response;
  } catch (error: any) {
    logEntry.status = error?.response?.status || 500;
    logEntry.statusText = error?.response?.statusText || error.message || 'Error';
    logEntry.response = error?.response?.data || error.message;
    logEntry.latency = Date.now() - startTime;
    logEntry.isError = true;

    const logs = (window as any).__apiLogs || [];
    logs.unshift(logEntry);
    (window as any).__apiLogs = logs.slice(0, 100);

    window.dispatchEvent(new CustomEvent('__apiLogsUpdated'));

    // Global 401 Unauthorized handling
    if (error?.response?.status === 401) {
      console.error('Unauthorized request (401). Redirecting to login...');

      // Prevent infinite redirect loops and bypass for public routes
      const isPublicRequest = config.url?.includes('/public/');
      
      // Prevent redirect if the user is already on a public-facing page
      const isPublicPage = 
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/register') ||
        window.location.pathname.includes('/reset-password') ||
        window.location.pathname.includes('/order/') ||
        window.location.pathname === '/';

      if (!isPublicPage && !isPublicRequest) {
        clearAuthSession();
        localStorage.setItem('userMustLogin', 'YES');
        window.location.href = '/login';
      }
    }

    // Do not unwrap the error. Let the caller handle the AxiosError.
    throw error;
  }
};

export default apiRequest;
